import genToken from "@utils/generateToken";

/**
 * Composable for executing async jobs with WebSocket feedback.
 *
 * Subscribes to an ActionCable channel, makes an HTTP request to start a
 * background job, and returns a Promise that resolves when the job completes
 * via WebSocket.
 *
 * Toast notifications are handled by the TaskProgress system (backend creates
 * a TaskProgress, the taskProgresses store picks it up via TaskProgressChannel).
 * This composable only handles the WebSocket promise lifecycle.
 *
 * @example Basic usage (default complete/error handler)
 * const { execute } = useAsyncJob();
 * await execute({
 *   cable: cableInstance,
 *   channel: "ReportsV2Channel",
 *   channelParams: { survey_token: "abc" },
 *   httpCall: async (clientId) => { ... },
 * });
 *
 * @example Streaming with custom message handler
 * await execute({
 *   cable: cableInstance,
 *   channel: "ReportsV2Channel",
 *   channelParams: { survey_token: "abc" },
 *   httpCall: async (clientId) => { ... },
 *   onMessage: (msg, { resolve, reject }) => {
 *     if (msg.action === "delta") streamingContent.value += msg.content;
 *     if (msg.action === "complete") resolve(msg);
 *     if (msg.action === "error") reject(new Error(msg.message));
 *   },
 * });
 */
export function useAsyncJob() {
  /**
   * @param {Object} options
   * @param {Object} options.cable - ActionCable instance
   * @param {Object} [options.vueInstance] - Vue component instance (from getCurrentInstance())
   * @param {string} options.channel - ActionCable channel name
   * @param {Object} [options.channelParams={}] - Additional params for channel subscription
   * @param {Function} options.httpCall - Async function receiving clientId, should return truthy on success
   * @param {Function} [options.onMessage] - Custom message handler. Receives (msg, { resolve, reject })
   * @returns {Promise<Object>} Resolves with the job result on success
   */
  async function execute(options) {
    const { cable, vueInstance, channel, channelParams = {}, httpCall, onMessage } = options;

    if (!cable) {
      throw new Error("useAsyncJob: cable instance is required");
    }

    const clientId = genToken();
    const handlerUid = `${vueInstance?.uid || "anonymous"}_${clientId}`;

    return new Promise((resolve, reject) => {
      let settled = false;

      const cleanup = () => {
        cable._removeChannel(channel, handlerUid);
      };

      const settle = (fn) => {
        if (!settled) {
          settled = true;
          cleanup();
          fn();
        }
      };

      const defaultMessageHandler = (msg) => {
        if (msg.client_id && msg.client_id !== clientId) return;

        if (msg.action === "complete") {
          settle(() => resolve(msg));
        } else if (msg.action === "error") {
          settle(() => reject(new Error(msg.message)));
        }
      };

      const customMessageHandler = (msg) => {
        if (msg.client_id && msg.client_id !== clientId) return;

        onMessage(msg, {
          resolve: (result) => settle(() => resolve(result)),
          reject: (error) => settle(() => reject(error)),
        });
      };

      cable.subscribe({
        channel,
        client_id: clientId,
        ...channelParams,
      });

      cable._addChannel(
        channel,
        { received: onMessage ? customMessageHandler : defaultMessageHandler },
        { _uid: handlerUid, _value: vueInstance?.proxy },
      );

      httpCall(clientId)
        .then((response) => {
          if (!response) {
            settle(() => reject(new Error("Failed to start job")));
          }
        })
        .catch((error) => {
          settle(() => reject(error));
        });
    });
  }

  return { execute };
}

export default useAsyncJob;
